// Types and image helpers for saved signatures and initials (Configuration > Signatures).
// Everything the server stores is a small transparent PNG, made here in the browser by drawing,
// typing or uploading.

export type CreatedVia = 'draw' | 'type' | 'upload';

export interface SavedSignature {
  full_name: string;
  created_via: CreatedVia;
  consented_at: string;
  signature_image: string; // PNG data URL
  initials_image: string; // PNG data URL
}

export interface SignatureInput {
  full_name: string;
  created_via: CreatedVia;
  signature_image: string;
  initials_image: string;
  consent: boolean;
}

export interface SignatureFont {
  key: string;
  label: string;
  family: string;
}

// Installed handwriting-style fonts differ by device, so each choice lists fallbacks. What the person
// sees in the preview is exactly the image that gets saved.
export const SIGNATURE_FONTS: SignatureFont[] = [
  { key: 'script', label: 'Classic', family: "'Snell Roundhand', 'Apple Chancery', 'Segoe Script', 'Brush Script MT', cursive" },
  { key: 'brush', label: 'Brush', family: "'Brush Script MT', 'Lucida Handwriting', 'Segoe Script', cursive" },
  { key: 'casual', label: 'Casual', family: "'Bradley Hand', 'Segoe Print', 'Marker Felt', cursive" },
  { key: 'formal', label: 'Formal', family: "'Zapfino', 'Lucida Calligraphy', 'Palatino Linotype', 'Palatino', serif" },
];

export const INK_COLOR = '#111c4e';
const MAX_WIDTH = 700;
const MAX_HEIGHT = 220;
const PADDING = 8;
const ALPHA_THRESHOLD = 12;
const NEAR_WHITE = 235;

// "Jane Q. Buyer" -> "JQB"; at most 3 letters.
export const initialsFrom = (fullName: string): string =>
  fullName
    .split(/\s+/)
    .map((part) => part.replace(/[^A-Za-z]/g, ''))
    .filter(Boolean)
    .map((part) => part[0].toUpperCase())
    .slice(0, 3)
    .join('');

// Scales a size down (never up) so it fits inside max, keeping its shape.
export const fitSize = (width: number, height: number, maxWidth = MAX_WIDTH, maxHeight = MAX_HEIGHT) => {
  const scale = Math.min(1, maxWidth / width, maxHeight / height);
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
};

// Smallest box around the visible (non-transparent) pixels, or null if there are none.
export const visibleBounds = (data: Uint8ClampedArray, width: number, height: number) => {
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (data[(y * width + x) * 4 + 3] > ALPHA_THRESHOLD) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  return maxX < 0 ? null : { x: minX, y: minY, width: maxX - minX + 1, height: maxY - minY + 1 };
};

const newCanvas = (width: number, height: number) => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
};

// Crops a canvas to its ink (plus a little margin) and returns a PNG data URL, or null when it is empty.
export const trimToPng = (source: HTMLCanvasElement): string | null => {
  const context = source.getContext('2d');
  if (!context) return null;
  const bounds = visibleBounds(context.getImageData(0, 0, source.width, source.height).data, source.width, source.height);
  if (!bounds) return null;

  const out = newCanvas(bounds.width + PADDING * 2, bounds.height + PADDING * 2);
  out.getContext('2d')?.drawImage(source, bounds.x, bounds.y, bounds.width, bounds.height, PADDING, PADDING, bounds.width, bounds.height);
  return out.toDataURL('image/png');
};

// Types the text in the chosen font as ink on a transparent background.
export const renderTypedPng = (text: string, font: SignatureFont): string | null => {
  const clean = text.trim();
  if (!clean) return null;
  const canvas = newCanvas(MAX_WIDTH, MAX_HEIGHT);
  const context = canvas.getContext('2d');
  if (!context) return null;
  context.fillStyle = INK_COLOR;
  context.textBaseline = 'middle';
  let size = 110;
  context.font = `${size}px ${font.family}`;
  // shrink long names so they fit on the canvas
  while (size > 24 && context.measureText(clean).width > MAX_WIDTH - 40) {
    size -= 6;
    context.font = `${size}px ${font.family}`;
  }
  context.fillText(clean, 20, MAX_HEIGHT / 2);
  return trimToPng(canvas);
};

// Turns an uploaded picture of a signature into a transparent PNG: scales it down, makes the white
// paper see-through and crops to the ink.
export const fileToSignaturePng = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      const size = fitSize(image.width, image.height);
      const canvas = newCanvas(size.width, size.height);
      const context = canvas.getContext('2d');
      if (!context) return reject(new Error('Your browser cannot read that image.'));
      context.drawImage(image, 0, 0, size.width, size.height);
      const pixels = context.getImageData(0, 0, size.width, size.height);
      for (let i = 0; i < pixels.data.length; i += 4) {
        if (pixels.data[i] > NEAR_WHITE && pixels.data[i + 1] > NEAR_WHITE && pixels.data[i + 2] > NEAR_WHITE) pixels.data[i + 3] = 0;
      }
      context.putImageData(pixels, 0, 0);
      const png = trimToPng(canvas);
      return png ? resolve(png) : reject(new Error('We could not find a signature in that image.'));
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('That file is not an image we can read. Use a PNG or JPG.'));
    };
    image.src = url;
  });

export const SIGNING_CONSENT_TEXT =
  'I agree that this electronic signature and these initials are the legal equivalent of my handwritten signature ' +
  'and initials, and that REIX may place them on contracts when I (or an agent I have allowed) create them.';
